import { compact } from 'lodash';
import React from 'react';
import { ExpansionOpportunitiesWithMap } from '../../components/ExpansionOpportunitiesWithMap';
import { IconName } from '../../components/Icon';
import { Account } from '../../features/Account';
import { AdminCenter } from '../../features/AdminCenter/AdminCenter';
import { PrincipalAdministration } from '../../features/AdminCenter/PrincipalAdministration/PrincipalAdministration';
import {
  APIKeyGenerator,
  APIKeyManagement,
  EDIInformation,
} from '../../features/Collaboration';
import { APIDocumentation } from '../../features/Collaboration/APIDocumentation';
import { SharedDocuments } from '../../features/Collaboration/SharedDocuments/SharedDocuments';
import CookieStatement from '../../features/CookieStatement/CookieStatement';
import { Dashboard } from '../../features/Dashboard';
import { GlobalUsers } from '../../features/GlobalUsers';
import { CustomerPortalInsights } from '../../features/Insights/CustomerPortalInsights/CustomerPortalInsights';
import { MarketInsightsTab } from '../../features/Insights/MarketInsightsTab';
import { SocialListeningTab } from '../../features/Insights/SocialListeningTab';
import { MemberDetails } from '../../features/PortalMembers/MemberDetails';
import { PortalMembers } from '../../features/PortalMembers/PortalMembers';
import TermsOfUse from '../../features/TermsOfUse/TermsOfUse';
import {
  Permission,
  PrimaryFeature,
  SecondaryFeature,
} from '../../utils/types';
import { SecondaryNavigation } from '../../components/SecondaryNavigation/SecondaryNavigation';
import { PowerBiReport } from '../../components/PowerBiReport';

export type MainMenuConfig = {
  section: 'primary' | 'secondary';
  order: number;
  title: string;
  icon: IconName;
};

export type InternalRoute = {
  routeId: FeatureType;
  url: string;
  name: string;
  Component: React.FC;
  authRequired: boolean;
  permissions?: Permission[];
  mainMenuConfig?: MainMenuConfig;
  additionalInformation: {
    trackingCategory: 'Partner Area' | 'User Area' | 'Legal';
    trackingType:
      | 'Overview'
      | 'Report'
      | 'List'
      | 'Account Info'
      | 'Info'
      | 'Cookie Statement'
      | 'Portal Members'
      | 'Global Users'
      | 'Operational Performance'
      | 'Customer portal'
      | 'Data Connections'
      | 'API Key Management'
      | 'Terms Of Use'
      | 'EDI Information';
  };
  secondaryRoutes?: SecondaryRoute[];
};

export type SecondaryRoute = {
  routeId: SecondaryFeature;
  component: React.FC;
};

export class InternalRouteCollection {
  constructor(public routes: InternalRoute[]) {}

  public find(id: PrimaryFeature | SecondaryFeature): InternalRoute {
    const route = this.routes.find(({ routeId }) => routeId === id);
    if (!route) {
      throw new Error(`Route ${id} not found`);
    }
    return route;
  }
}

export type FeatureType = PrimaryFeature | SecondaryFeature;

// This determines whether a route item should be in the main menu
// It also guarrantees to Typescript that these items WILL have mainMenuConfigs and permissions
export const hasMainMenuConfig = (
  i: InternalRoute,
): i is InternalRoute & {
  mainMenuConfig: MainMenuConfig;
} => !!i.mainMenuConfig;

export const encodePrincipalIdToUrl = (principalId?: string): string =>
  encodeURI(principalId?.toLowerCase() || '');

//selectedPrincipal can be undefined e.g. for cookie-statement route
export const routes = (selectedPrincipal?: string): InternalRouteCollection => {
  const selectedPrincipalUrl = selectedPrincipal ? '/' + selectedPrincipal : '';

  return new InternalRouteCollection([
    {
      routeId: PrimaryFeature.DASHBOARD,
      url: `${selectedPrincipalUrl}/`,
      name: 'Dashboard',
      Component: Dashboard,
      authRequired: true,
      permissions: [Permission.DASHBOARD_VIEW],
      mainMenuConfig: {
        section: 'primary',
        order: 1,
        title: 'dashboard',
        icon: 'Home',
      },
      additionalInformation: {
        trackingCategory: 'Partner Area',
        trackingType: 'Overview',
      },
    },
    {
      routeId: PrimaryFeature.INSIGHTS,
      url: `${selectedPrincipalUrl}/insights`,
      name: 'Insights',
      Component: SecondaryNavigation,
      authRequired: true,
      mainMenuConfig: {
        section: 'primary',
        order: 4,
        title: 'insights',
        icon: 'Insights',
      },
      additionalInformation: {
        trackingCategory: 'Partner Area',
        trackingType: 'Report',
      },
      secondaryRoutes: [
        {
          routeId: SecondaryFeature.INSIGHTS_MARKET_INSIGHTS,
          component: MarketInsightsTab,
        },
        {
          routeId: SecondaryFeature.INSIGHTS_EXPANSION_OPPORTUNITIES,
          component: ExpansionOpportunitiesWithMap,
        },
        {
          routeId: SecondaryFeature.INSIGHTS_CUSTOMER_PORTAL_INSIGHTS,
          component: CustomerPortalInsights,
        },
        {
          routeId: SecondaryFeature.INSIGHTS_SOCIAL_LISTENING,
          component: SocialListeningTab,
        },
        {
          routeId: SecondaryFeature.INSIGHTS_PRODUCT_FEEDBACK,
          component: PowerBiReport,
        },
      ],
    },
    {
      routeId: PrimaryFeature.ACCOUNT,
      url: `${selectedPrincipalUrl}/account`,
      name: 'User Overview',
      Component: Account,
      authRequired: true,
      permissions: [],
      mainMenuConfig: {
        section: 'secondary',
        order: 3,
        title: 'account',
        icon: 'Account',
      },
      additionalInformation: {
        trackingCategory: 'User Area',
        trackingType: 'Account Info',
      },
    },
    {
      routeId: PrimaryFeature.COOKIE_STATEMENT,
      url: `${selectedPrincipalUrl}/cookie-statement`,
      name: 'Cookie Statement',
      Component: CookieStatement,
      authRequired: false,
      additionalInformation: {
        trackingCategory: 'User Area',
        trackingType: 'Cookie Statement',
      },
    },
    {
      routeId: PrimaryFeature.TERMS_OF_USE,
      url: `${selectedPrincipalUrl}/terms-of-use`,
      name: 'Terms Of Use',
      Component: TermsOfUse,
      authRequired: false,
      additionalInformation: {
        trackingCategory: 'User Area',
        trackingType: 'Terms Of Use',
      },
    },
    {
      routeId: PrimaryFeature.API_DOCUMENTATION,
      url: `${selectedPrincipalUrl}/api-documentation`,
      name: 'API Documentation',
      Component: APIDocumentation,
      authRequired: true,
      permissions: [Permission.DATA_CONNECTION_DEVELOPER_SCREENS],
      mainMenuConfig: {
        section: 'primary',
        order: 8,
        title: 'api-documentation',
        icon: 'Documentation',
      },
      additionalInformation: {
        trackingCategory: 'Partner Area',
        trackingType: 'List',
      },
    },
    {
      routeId: PrimaryFeature.ADMIN_CENTER,
      url: `/admin-center`,
      name: 'Admin Center',
      Component: AdminCenter,
      authRequired: true,
      mainMenuConfig: {
        section: 'secondary',
        order: 1,
        title: 'admin-center',
        icon: 'Administration',
      },
      additionalInformation: {
        trackingCategory: 'User Area',
        trackingType: 'Portal Members',
      },
    },

    {
      routeId: SecondaryFeature.PRINCIPAL_ADMINISTRATION,
      url: `/admin-center/principal-administration`,
      name: 'Principal Administration',
      Component: PrincipalAdministration,
      authRequired: true,
      additionalInformation: {
        trackingCategory: 'User Area',
        trackingType: 'Global Users',
      },
    },
    {
      routeId: SecondaryFeature.GLOBAL_USERS,
      url: `/admin-center/global-users`,
      name: 'Global Users',
      Component: GlobalUsers,
      authRequired: true,
      permissions: [Permission.EDIT_GLOBAL_USERS],
      additionalInformation: {
        trackingCategory: 'User Area',
        trackingType: 'Portal Members',
      },
    },
    {
      routeId: PrimaryFeature.PORTAL_MEMBERS,
      url: `${selectedPrincipalUrl}/portal-members`,
      name: 'Portal Members',
      Component: PortalMembers,
      authRequired: true,
      permissions: [
        Permission.USER_ROLE_MGMT_EDIT,
        Permission.USER_ROLE_MGMT_VIEW,
      ],
      mainMenuConfig: {
        section: 'secondary',
        order: 5,
        title: 'portal-members',
        icon: 'Settings',
      },
      additionalInformation: {
        trackingCategory: 'User Area',
        trackingType: 'Portal Members',
      },
    },
    {
      routeId: PrimaryFeature.REPORTING,
      url: `${selectedPrincipalUrl}/reporting`,
      name: 'Reporting',
      Component: SecondaryNavigation,
      authRequired: true,
      mainMenuConfig: {
        section: 'primary',
        order: 2,
        title: 'reporting',
        icon: 'Reporting',
      },
      additionalInformation: {
        trackingCategory: 'Partner Area',
        trackingType: 'Operational Performance',
      },
      secondaryRoutes: [
        {
          routeId: SecondaryFeature.REPORTING_SALES,
          component: PowerBiReport,
        },
        {
          routeId: SecondaryFeature.REPORTING_TARGETS,
          component: PowerBiReport,
        },
        {
          routeId: SecondaryFeature.REPORTING_OPPORTUNITIES,
          component: PowerBiReport,
        },
        {
          routeId: SecondaryFeature.REPORTING_LEADS,
          component: PowerBiReport,
        },
        {
          routeId: SecondaryFeature.REPORTING_PRODUCT_SAMPLES,
          component: PowerBiReport,
        },
        {
          routeId: SecondaryFeature.REPORTING_SUPPLY_CHAIN,
          component: PowerBiReport,
        },
        {
          routeId: SecondaryFeature.REPORTING_INCIDENTS,
          component: PowerBiReport,
        },
      ],
    },

    {
      routeId: PrimaryFeature.LABS_AND_FORMULATIONS,
      url: `${selectedPrincipalUrl}/labs-and-formulations`,
      name: 'Labs & Formulations',
      Component: SecondaryNavigation,
      authRequired: true,
      mainMenuConfig: {
        section: 'primary',
        order: 5,
        title: 'labs-and-formulations',
        icon: 'LabsAndFormulations',
      },
      additionalInformation: {
        trackingCategory: 'Partner Area',
        trackingType: 'Operational Performance',
      },
      secondaryRoutes: [
        {
          routeId: SecondaryFeature.LABS_AND_FORMULATIONS_FORMULATIONS,
          component: PowerBiReport,
        },
      ],
    },

    {
      routeId: PrimaryFeature.PORTAL_MEMBERS,
      url: `${selectedPrincipalUrl}/portal-members/:userId`,
      name: 'Member details',
      Component: MemberDetails,
      authRequired: true,
      permissions: [
        Permission.USER_ROLE_MGMT_EDIT,
        Permission.USER_ROLE_MGMT_VIEW,
      ],
      additionalInformation: {
        trackingCategory: 'User Area',
        trackingType: 'Portal Members',
      },
    },
    {
      routeId: PrimaryFeature.COLLABORATION,
      url: `${selectedPrincipalUrl}/collaboration`,
      name: 'Collaboration',
      Component: SecondaryNavigation,
      authRequired: true,
      permissions: [Permission.DASHBOARD_VIEW],
      mainMenuConfig: {
        section: 'primary',
        order: 6,
        title: 'collaboration',
        icon: 'Collaboration',
      },
      additionalInformation: {
        trackingCategory: 'User Area',
        trackingType: 'Data Connections',
      },
      secondaryRoutes: [
        {
          routeId: SecondaryFeature.COLLABORATION_SHARED_DOCUMENTS,
          component: SharedDocuments,
        },
        {
          routeId: SecondaryFeature.COLLABORATION_API_MANAGEMENT,
          component: APIKeyManagement,
        },
        {
          routeId: SecondaryFeature.COLLABORATION_EDI_INFORMATION,
          component: EDIInformation,
        },
      ],
    },
    {
      routeId: PrimaryFeature.API_KEY_GENERATOR,
      url: `${selectedPrincipalUrl}/partner-area/api-key-generator`,
      name: 'Partner Area',
      Component: APIKeyGenerator,
      authRequired: true,
      permissions: [Permission.DATA_CONNECTION_DEVELOPER_SCREENS],
      additionalInformation: {
        trackingCategory: 'Partner Area',
        trackingType: 'API Key Management',
      },
      mainMenuConfig: {
        section: 'primary',
        order: 7,
        title: 'partner-area',
        icon: 'Key',
      },
    },
  ]);
};

//All routes which should work without the url principal prefix
const testPattern = '_t1e2s3t4_';
export const ROUTES_WITHOUT_PRINCIPAL_PREFIX = compact(
  routes(testPattern).routes.map(
    (route) => !route.url.includes(testPattern) && route.url,
  ),
);

export const findRouteDefintionByPathname = (
  pathname: string,
  selectedPrincipal?: string,
): InternalRoute => {
  const routesValues = routes(selectedPrincipal).routes;

  for (const route of routesValues) {
    if (route.url === pathname) {
      return route;
    }
  }

  // return Dashboard as default Route

  return routes(selectedPrincipal).routes[0];
};
